import { useState, useEffect } from 'react';
import { Key, Eye, EyeOff, Plus, Trash2, Copy, Shield, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { settingsService } from '../../services/settingsService';
import type { SecretCreateRequest } from '../../services/settingsService';
import type { SecretData } from '../../utils/encryption';
import { toast } from 'react-hot-toast';

const SecretsTab = () => {
  const [secrets, setSecrets] = useState<SecretData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newSecret, setNewSecret] = useState({ name: '', value: '', environment: 'development' as 'development' | 'staging' | 'production' });
  const [showAddForm, setShowAddForm] = useState(false);
  const [maskedSecrets, setMaskedSecrets] = useState<Set<string>>(new Set());
  const [operationLoading, setOperationLoading] = useState<string | null>(null);

  useEffect(() => {
    const loadSecrets = async () => {
      setLoading(true);
      setError(null);
      try {
        const loadedSecrets = await settingsService.loadSecrets();
        setSecrets(loadedSecrets);
        const initialMasked = new Set(loadedSecrets.map(secret => secret.id));
        setMaskedSecrets(initialMasked);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load secrets';
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };
    loadSecrets();
  }, []);

  const toggleSecretVisibility = (id: string) => {
    setMaskedSecrets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const copySecret = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success('Secret copied to clipboard');
    } catch {
      toast.error('Failed to copy secret');
    }
  };

  const deleteSecret = async (id: string) => {
    setOperationLoading(`delete-${id}`);
    try {
      await settingsService.deleteSecret(id);
      setSecrets(prev => prev.filter(secret => secret.id !== id));
      setMaskedSecrets(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
      toast.success('Secret deleted successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete secret';
      toast.error(errorMessage);
    } finally {
      setOperationLoading(null);
    }
  };

  const addSecret = async () => {
    if (!newSecret.name || !newSecret.value) {
      toast.error('Please fill in all fields');
      return;
    }

    setOperationLoading('add');
    try {
      const secretRequest: SecretCreateRequest = {
        name: newSecret.name,
        value: newSecret.value,
        environment: newSecret.environment
      };
      
      const savedSecret = await settingsService.saveSecret(secretRequest);
      setSecrets(prev => [...prev, savedSecret]);
      setMaskedSecrets(prev => new Set([...prev, savedSecret.id]));
      setNewSecret({ name: '', value: '', environment: 'development' });
      setShowAddForm(false);
      toast.success('Secret added successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add secret';
      toast.error(errorMessage);
    } finally {
      setOperationLoading(null);
    }
  };

  const environments = ['development', 'staging', 'production'];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold violett-gradient-text text-drop-shadow">Secrets</h2>
        <p className="text-slate-400 mt-1">Manage API keys, tokens, and other sensitive configuration</p>
      </div>

      <Card className="bg-slate-900/40 backdrop-blur-sm border border-slate-700/30 rounded-xl p-6 hover:border-slate-600/50 transition-all duration-300">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Key size={20} className="text-brand-accent" />
              <span>Environment Variables</span>
            </div>
            <Button 
              onClick={() => setShowAddForm(!showAddForm)} 
              size="sm"
              disabled={loading || operationLoading !== null}
            >
              {operationLoading === 'add' ? (
                <Loader2 size={16} className="mr-2 animate-spin" />
              ) : (
                <Plus size={16} className="mr-2" />
              )}
              Add Secret
            </Button>
          </CardTitle>
          <CardDescription>Securely store and manage your API keys and secrets</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {showAddForm && (
            <div className="p-4 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 rounded-lg border border-brand-primary/20">
              <h4 className="text-sm font-medium cream-gradient-text text-drop-shadow mb-4">Add New Secret</h4>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Name</label>
                    <Input
                      value={newSecret.name}
                      onChange={(e) => setNewSecret(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="API_KEY_NAME"
                      className="violett-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Environment</label>
                    <select
                      value={newSecret.environment}
                      onChange={(e) => setNewSecret(prev => ({ ...prev, environment: e.target.value as 'development' | 'staging' | 'production' }))}
                      className="violett-input"
                    >
                      {environments.map(env => (
                        <option key={env} value={env}>{env}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Value</label>
                  <Input
                    type="password"
                    value={newSecret.value}
                    onChange={(e) => setNewSecret(prev => ({ ...prev, value: e.target.value }))}
                    placeholder="Enter secret value..."
                    className="violett-input"
                  />
                </div>
                <div className="flex space-x-3">
                  <Button 
                    onClick={addSecret}
                    disabled={operationLoading === 'add' || !newSecret.name || !newSecret.value}
                  >
                    {operationLoading === 'add' ? (
                      <>
                        <Loader2 size={16} className="mr-2 animate-spin" />
                        Adding...
                      </>
                    ) : (
                      'Add Secret'
                    )}
                  </Button>
                  <Button 
                    variant="ghost" 
                    onClick={() => setShowAddForm(false)}
                    disabled={operationLoading === 'add'}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={24} className="animate-spin text-brand-accent" />
              <span className="ml-2 text-slate-400">Loading secrets...</span>
            </div>
          ) : error ? (
            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-red-400 text-sm">{error}</p>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => window.location.reload()}
                className="mt-2"
              >
                Retry
              </Button>
            </div>
          ) : secrets.length === 0 ? (
            <div className="text-center py-8">
              <Key size={48} className="mx-auto text-slate-600 mb-4" />
              <p className="text-slate-400">No secrets found</p>
              <p className="text-slate-500 text-sm">Add your first secret to get started</p>
            </div>
          ) : (
            <div className="space-y-3">
              {secrets.map((secret) => (
                <div key={secret.id} className="flex items-center justify-between p-4 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 rounded-lg">
                  <div className="flex items-center space-x-3 flex-1">
                    <div className="p-2 bg-brand-primary/20 rounded-lg">
                      <Key size={16} className="text-brand-accent" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-sm font-medium text-brand-text">{secret.name}</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          secret.environment === 'production' ? 'bg-red-400/20 text-red-400' :
                          secret.environment === 'staging' ? 'bg-yellow-400/20 text-yellow-400' :
                          'bg-green-400/20 text-green-400'
                        }`}>
                          {secret.environment}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <code className="text-xs text-slate-400 font-mono">
                          {maskedSecrets.has(secret.id) ? '••••••••••••••••' : secret.value}
                        </code>
                        <span className="text-xs text-slate-500">Last used: {secret.lastUsed}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleSecretVisibility(secret.id)}
                      disabled={operationLoading !== null}
                    >
                      {maskedSecrets.has(secret.id) ? <Eye size={14} /> : <EyeOff size={14} />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copySecret(secret.value)}
                      disabled={operationLoading !== null}
                    >
                      <Copy size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteSecret(secret.id)}
                      className="text-red-400 hover:text-red-300"
                      disabled={operationLoading !== null}
                    >
                      {operationLoading === `delete-${secret.id}` ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Trash2 size={14} />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-slate-900/40 backdrop-blur-sm border border-slate-700/30 rounded-xl p-6 hover:border-slate-600/50 transition-all duration-300">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Shield size={20} className="text-brand-accent" />
            <span>Security Guidelines</span>
          </CardTitle>
          <CardDescription>Best practices for managing secrets</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <h4 className="text-sm font-medium text-blue-400 mb-2">🔒 Security Tips</h4>
              <ul className="text-sm text-slate-400 space-y-1">
                <li>• Use environment-specific secrets (dev, staging, prod)</li>
                <li>• Rotate API keys regularly</li>
                <li>• Never commit secrets to version control</li>
                <li>• Use least-privilege access for API keys</li>
              </ul>
            </div>
            
            <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <h4 className="text-sm font-medium text-yellow-400 mb-2">⚠️ Important Notes</h4>
              <ul className="text-sm text-slate-400 space-y-1">
                <li>• Secrets are encrypted at rest and in transit</li>
                <li>• Only you can view your secret values</li>
                <li>• Deleted secrets cannot be recovered</li>
                <li>• Audit logs track all secret access</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SecretsTab;
